import { EditOutlined } from '@ant-design/icons';
import { useHead } from '@unhead/react';
import { message, Result, Tooltip } from 'antd';
import classNames from 'classnames';
import { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { ReportListPageTemplate } from 'business/shift-report/report/components/report-list-template';
import ReportList, {
  DEFAULT_PAGE_LIMIT,
} from 'business/shift-report/report/components/ReportList';
import { SEARCH_PARAM_KEY_MAP } from 'business/shift-report/report/hooks/constants';
import { useFilterParams } from 'business/shift-report/report/hooks/use-filter-params';
import { getRouteRedirection } from 'business/shift-report/report/services/get-route-redirection';
import Routes from 'config/routes';
import { Module_Enum, useGetReportsQuery } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import { SIMPLE_DATE_FORMAT } from 'technical/string/formatter';
import Button from 'ui/button';
import { DatePicker } from 'ui/form/date-picker';

import styles from './index.module.scss';

function ReportListPage() {
  const { t } = useTranslation();

  const { isMobile } = useMediaType();

  useHead({ title: t('user.menu.sections.reporting.title') });

  const gqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const { currentConstructionSite } = useAppContext();
  const {
    modules: {
      SHIFT_REPORT: { hasManagerReadPermission },
    },
  } = usePermissions();

  const {
    currentPage,
    startDate,
    updateSearchParam,
    emptyFilters,
    getQueryParamString,
  } = useFilterParams();

  function filterResults(value: Dayjs | null) {
    if (value === null) {
      emptyFilters();
      return;
    }

    updateSearchParam(
      SEARCH_PARAM_KEY_MAP.startDate,
      value.format(SIMPLE_DATE_FORMAT),
    );
  }

  const { loading, error, data, refetch } = useGetReportsQuery({
    variables: {
      constructionSiteId: currentConstructionSite?.id,
      limit: DEFAULT_PAGE_LIMIT,
      offset: (currentPage - 1) * DEFAULT_PAGE_LIMIT,
      date: startDate?.format(SIMPLE_DATE_FORMAT),
    },
    fetchPolicy: 'network-only',
    skip: !currentConstructionSite?.id,
    context: {
      headers: gqlHeaders,
    },
  });

  useEffect(
    function alertUser() {
      if (error) {
        message.error(error.message);
      }
    },
    [error],
  );

  const emptyListComponent = (
    <Result
      status="404"
      title={t('pages.report.list.filterNotFound.title')}
      subTitle={
        startDate === null
          ? t('pages.report.list.noReport.label')
          : t('pages.report.list.filterNotFound.label')
      }
      extra={
        <Button onClick={() => emptyFilters()}>
          {t('pages.report.list.filterNotFound.button')}
        </Button>
      }
    />
  );

  const newReportButton = isMobile ? (
    <Tooltip
      title={t('pages.report.startNewReport.tooltip')}
      className="button-center"
    >
      <Button type="primary" icon={<EditOutlined />} disabled>
        {t('pages.report.startNewReport.action')}
      </Button>
    </Tooltip>
  ) : (
    <Link
      to={
        hasManagerReadPermission ? Routes.ReportCreate : Routes.ReportUserHome
      }
      className="button-center"
    >
      <Button type="primary" icon={<EditOutlined />}>
        {t('pages.report.startNewReport.action')}
      </Button>
    </Link>
  );

  return (
    <ReportListPageTemplate>
      <DatePicker
        className={classNames(styles.searchBox, 'button-center')}
        format="L"
        value={startDate}
        onChange={filterResults}
        allowClear
      />
      <ReportList
        loading={loading}
        refetchList={refetch}
        shiftReports={data?.shiftReport}
        paginationMeta={{
          shiftReportCount: data?.shiftReportAggregate.aggregate?.count || 0,
        }}
        emptyListComponent={emptyListComponent}
        routeRedirection={getRouteRedirection(
          Routes.ReportList,
          getQueryParamString(),
        )}
      />
      {!currentConstructionSite?.isCompleted ? newReportButton : null}
    </ReportListPageTemplate>
  );
}

export default ReportListPage;
